/* eslint-disable react/prop-types */
import { forwardRef } from 'react';
import { FaCheckCircle, FaClock } from 'react-icons/fa';
import { Alert, Tooltip } from '@mui/material';

const STATUS_TOOLTIP_MESSAGES: Record<'project' | 'os' | 'apps', { updated: string; outOfDate: string }> = {
  project: {
    updated: `Device’s Mason configuration matches it’s group’s version`,
    outOfDate: `Device's Mason configuration does not match it’s group’s version.`,
  },
  os: {
    updated: `Device’s Mason OS matches it’s group’s version`,
    outOfDate: `Device's Mason OS does not match it’s group’s version. Device’s version may be an older OR more recent version`,
  },
  apps: {
    updated: `Device’s applications match it’s group’s versions`,
    outOfDate: `Device's applications do not match it’s group’s versions`,
  },
};

interface UpdateStatusProps {
  value?: boolean;
  trueText?: string;
  falseText?: string;
}

export const UpdateStatus = forwardRef<HTMLDivElement, UpdateStatusProps>(function UpdateStatus(
  { value = false, trueText = 'Updated', falseText = 'Out of Date' },
  ref
) {
  return (
    <Alert
      ref={ref}
      variant='standard'
      severity={value ? 'success' : 'warning'}
      icon={value ? <FaCheckCircle /> : <FaClock />}
      // All proportions and alignments should hold as parent fontsize goes up
      sx={{
        lineHeight: '1.2em',
        [`&.MuiAlert-standard${value ? 'Success' : 'Warning'}`]: {
          padding: 0,
          lineHeight: '1.2em',
          fontSize: '1em',
          color: 'currentColor',
          backgroundColor: 'transparent',
        },
        '&& .MuiAlert-icon': {
          padding: 0,
          lineHeight: '1.2em',
          fontSize: '1.2em',
          mr: 0.5,
          borderRadius: '1em',
          backgroundColor: 'transparent',
          color: (theme) => (value ? theme.palette.success.dark : theme.palette.warning.dark),
        },
        '& .MuiAlert-message': {
          padding: 0,
          lineHeight: '1.2em',
          overflow: 'visible',
        },
      }}
    >
      {value ? trueText : falseText}
    </Alert>
  );
});

interface UpdateStatusWithTooltipProps extends UpdateStatusProps {
  statusType: 'project' | 'os' | 'apps';
}

export const UpdateStatusWithTooltip = ({ statusType, value, ...props }: UpdateStatusWithTooltipProps) => {
  const messageUpdated = STATUS_TOOLTIP_MESSAGES[statusType].updated;
  const messageOutOfDate = STATUS_TOOLTIP_MESSAGES[statusType].outOfDate;

  return (
    <Tooltip title={value ? messageUpdated : messageOutOfDate} arrow placement='top-start'>
      <UpdateStatus value={value} {...props} />
    </Tooltip>
  );
};
